import React, { useState, useEffect } from 'react';
import { useAuth } from './../Context/AuthContext';
import { Toast, ToastContainer } from 'react-bootstrap';
import { BASE_URL } from '../constants';
const LocationChecker = () => {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shelterLocation, setShelterLocation] = useState(null);
  const [isSwitchOn, setIsSwitchOn] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('error');
  const { user } = useAuth();
  const shelterId = user?.shelter?.id;
  const userId = user?.id;
  let watchId = null;

  // Load data from localStorage on component mount
  useEffect(() => {
    const savedStatus = localStorage.getItem('locationStatus');
    const savedShelterLocation = localStorage.getItem('shelterLocation');
    const savedSwitchState = localStorage.getItem('switchState') === 'on';

    if (savedStatus) {
      setStatus(savedStatus);
    }
    if (savedShelterLocation) {
      setShelterLocation(JSON.parse(savedShelterLocation));
    }
    setIsSwitchOn(savedSwitchState);
    setLoading(false);

    // Check geolocation permission right when the component loads
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((permissionStatus) => {
        if (permissionStatus.state === 'granted') {
          setIsSwitchOn(true);
        } else {
          setIsSwitchOn(false);
        }

        permissionStatus.onchange = () => {
          if (permissionStatus.state === 'granted') {
            setIsSwitchOn(true); 
          } else {
            setIsSwitchOn(false); 
          }
        };
      });
    }

    // Watch for location updates if the switch is on
    if ('geolocation' in navigator) {
      watchId = navigator.geolocation.watchPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
    
        if (user?.isRefugee) {
            // Compare refugee location to shelter
            await checkLocation(latitude, longitude);
          }
    
          setError(null); // Clear any previous errors
        },
        (err) => {
          setError(`Error getting location: ${err.message}`);
          setIsSwitchOn(false);
          localStorage.removeItem('switchState');
        },
        { enableHighAccuracy: true }
      );
    }
    
    return () => {
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, []);

  // Check user's location relative to the shelter
  const checkLocation = async (latitude, longitude) => {
    if (!user?.isRefugee) return;
    try {
      const response = await fetch(`${BASE_URL}/shelters/check-location`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ latitude, longitude, userId, shelterId }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch location status');
      }

      const data = await response.json();
      const locationStatus = data.isInside ? 'inside' : 'outside';
      setStatus(locationStatus);
      localStorage.setItem('locationStatus', locationStatus);
    } catch (err) {
      setError(`Error checking location: ${err.message}`);
    }
  };

  // Send location data to the server


  // Toggle switch for location tracking
  const handleToggle = (shouldTurnOn) => {
    if (shouldTurnOn) {
      setIsSwitchOn(true);
      localStorage.setItem('switchState', 'on');
    } else {
      setIsSwitchOn(false);
      localStorage.removeItem('switchState');
      localStorage.removeItem('locationStatus');
      localStorage.removeItem('shelterLocation');
      setStatus(null);
      setShelterLocation(null);
    }
  };

  // Handle switch change and alert user if location is denied
  const handleSwitchChange = () => {
    if (!isSwitchOn) {
      // User is turning the switch "on"
      if (navigator.permissions) {
        navigator.permissions.query({ name: 'geolocation' }).then((permissionStatus) => {
          if (permissionStatus.state === 'denied') {
            setToastMessage('Location access is denied. Please enable location services in your browser settings.');
            setToastType('error');
            setShowToast(true);
          } else {
            navigator.geolocation.getCurrentPosition(
              () => handleToggle(true),
              (error) => {
                if (error.code === error.PERMISSION_DENIED) {
                  setToastMessage('Location access is denied. Please enable location services in your browser settings.');
                  setToastType('error');
                  setShowToast(true);
                }
              }
            );
          }
        });
      } else {
        navigator.geolocation.getCurrentPosition(
          () => handleToggle(true),
          (error) => {
            if (error.code === error.PERMISSION_DENIED) {
              setToastMessage('Location access is denied. Please enable location services in your browser settings.');
              setToastType('error');
              setShowToast(true);
            }
          }
        );
      }
    } else {
      // User is turning the switch "off"
      if (navigator.permissions) {
        navigator.permissions.query({ name: 'geolocation' }).then((permissionStatus) => {
          if (permissionStatus.state === 'granted') {
            setToastMessage('Location tracking is required. You cannot disable it.');
            setToastType('error');
            setShowToast(true);
          } else {
            handleToggle(false);
          }
        });
      } else {
        setToastMessage('Location tracking is required. You cannot disable it.');
        setToastType('error');
        setShowToast(true);
      }
    }
  };

  return (
    <>
      {/* Only keep the condition for rendering */}
      {showToast && (
        <ToastContainer position="top-center" className="p-3">
          <Toast show={showToast} onClose={() => setShowToast(false)} bg={toastType} delay={3000} autohide>
            <Toast.Body>{toastMessage}</Toast.Body>
          </Toast>
        </ToastContainer>
      )}
    </>
  );
};

export default LocationChecker;
